// Strings for identity / billing / integrations / PQ screens. Component-local
// (useFx) like smartdocs.ts, so they never collide with the global bundles.
// Missing keys fall back to the global scope; non-EN/PL locales fall back to EN.
import { useI18n } from 'vue-i18n'

const en = {
  common: {
    cancel: 'Cancel', save: 'Save', saved: 'Saved', copy: 'Copy', copied: 'Copied', dismiss: 'Dismiss',
    refresh: 'Refresh', load_more: 'Load more', failed: 'Something went wrong. Try again.',
    load_failed: 'Could not load data.', email: 'Email', role: 'Role', full_name: 'Full name',
    password: 'Password', go_to_app: 'Go to dashboard', go_to_sign_in: 'Go to sign in',
  },
  roles: { admin: 'Admin', member: 'Member' },
  team_roles: { TEAM_ADMIN: 'Team admin', TEAM_MANAGER: 'Manager', TEAM_MEMBER: 'Member', TEAM_VIEWER: 'Viewer' },
  invite: {
    title: 'Invitation',
    invalid: 'This invitation link is invalid or has already been used.',
    expired: 'This invitation has expired. Ask the sender for a new one.',
    join: 'Join {org}',
    invited_by: '{name} invited you.',
    team: 'Team: {team}.',
    expires: 'Expires',
    accept: 'Accept invitation',
    mismatch: 'You are signed in as {current}, but this invitation is for {invited}.',
    mismatch_short: 'This invitation was sent to a different email address.',
    switch_account: 'Sign out and use another account',
    sign_in_hint: 'You already have a sign.yt account with this email. Sign in to accept.',
    sign_in_to_accept: 'Sign in to accept',
    create_hint: 'Create your account to join. Your email is fixed by the invitation.',
    password_hint: 'At least 10 characters.',
    password_short: 'Password must be at least 10 characters.',
    create_and_join: 'Create account and join',
    account_exists: 'An account with this email already exists. Sign in instead.',
  },
  verify_email: {
    banner: 'Confirm your email address {email} to keep full access.',
    resend: 'Resend email',
    sent: 'Verification email sent to {email}.',
    checking: 'Confirming your email…',
    ok_title: 'Email confirmed',
    ok_body: 'Thanks. Your email address is verified.',
    error_title: 'Link invalid or expired',
    error_body: 'Sign in and request a new verification email from the banner.',
  },
  team_invite: {
    title: 'Invite people',
    subtitle: 'Invitees get an email link. Existing members of this organization are added directly.',
    team: 'Team (optional)',
    team_role: 'Team role',
    no_team: 'No team',
    send: 'Send invite',
    sent: 'Invitation sent to {email}',
    added: '{email} added to the team',
    failed: 'Could not send the invitation',
    pending: 'Pending invitations ({n})',
    none: 'No pending invitations.',
    sent_on: 'Sent {date} · expires {exp}',
    resend: 'Resend',
    revoke: 'Revoke',
    revoke_aria: 'Revoke invitation for {email}',
    revoke_title: 'Revoke invitation',
    revoke_confirm: 'The link sent to {email} will stop working.',
  },
  sessions: {
    title: 'Active sessions',
    subtitle: 'Devices currently signed in to your account.',
    this_device: 'This device',
    last_active: 'Last active {date}',
    unknown_device: 'Unknown device',
    browser_on_os: '{browser} on {os}',
    revoke: 'Sign out',
    revoke_aria: 'Sign out {device}',
    revoke_all: 'Sign out other devices',
    revoke_all_confirm: 'Every session except this one will be signed out.',
    revoked_all: 'Other devices signed out',
  },
  rodo: {
    email_export_link: 'Email me a download link',
    email_delete_link: 'Email me a deletion link',
    proof_sent_title: 'Check your inbox',
    proof_sent: 'We sent a one-time confirmation link to {email}. It expires in 1 hour.',
    action: 'Request type',
    action_export: 'Export my data',
    action_delete: 'Delete my account',
    delete_title: 'Delete your account',
    delete_body: 'Your personal data will be anonymized. Signed documents are archived as required by law. This cannot be undone.',
    type_delete: 'Type DELETE to confirm',
    delete_confirm_btn: 'Delete my account',
    deleted: 'Your account has been deleted and your data anonymized.',
  },
  orgdata: {
    tab: 'Data & privacy',
    export_title: 'Export organization data',
    export_body: 'One ZIP with every document (original and signed PDFs), audit trails, the full audit log, recipients, templates, contacts and settings (without credentials). We email you a download link valid for 7 days that stops working 10 minutes after the first download.',
    export_btn: 'Request export',
    requested: 'Requested {date}',
    size: 'Size: {size}',
    status_PENDING: 'Queued…',
    status_RUNNING: 'Preparing… {progress}%',
    status_READY: 'Ready. Check your email for the download link (expires {date}).',
    status_DOWNLOADED: 'Downloaded {date}.',
    status_EXPIRED: 'The download link expired.',
    status_FAILED: 'The export failed. Try again.',
    status_FAILED_EMAIL: 'Ready, but we could not email the link. Send it again.',
    resend_btn: 'Email the link again',
    resent: 'Download link sent',
    step_up_sent: 'We emailed you a confirmation code. Enter it to continue.',
    download_hint: 'The download starts in your browser. If it breaks off, use the button again within 10 minutes.',
    in_progress: 'An export is already being prepared.',
    rate_limited: 'Too many requests. Try again in a few minutes.',
    mfa_label: 'Authentication code',
    mfa_hint: 'Enter your authentication code to confirm.',
    mfa_invalid: 'Invalid code. Try again.',
    close_title: 'Close organization',
    close_body: 'Locks access for all members at once, cancels the subscription and permanently deletes all documents and data after a 30-day grace period. Export your data first.',
    close_type: 'Type {name} to confirm',
    close_btn: 'Close organization',
    close_confirm: 'All members lose access immediately. This cannot be undone by you.',
    closed: 'Organization closed. You are being signed out.',
    name_mismatch: 'The name does not match.',
    download_title: 'Organization export',
    download_btn: 'Download export',
    link_invalid: 'This link is invalid, expired or already used. Request a new export in Settings, Data & privacy.',
    closure_badge: 'Closure requested',
    closure_hint: 'Closed by the organization admin on {date}. Enable the organization to cancel the closure before the purge date.',
  },
  pq: {
    title: 'Evidence seal',
    badge: 'Post-quantum-ready evidence seal',
    explainer: 'The final PDF hash and audit trail are sealed with ML-DSA-65 and Ed25519. This is tamper evidence, not a qualified electronic signature.',
    alg: 'Algorithm',
    kid: 'Key ID',
    download: 'Download seal (.json)',
    verify_link: 'Verify a copy',
    verify_title: 'Post-quantum evidence seal',
    verify_explainer: 'Checks the post-quantum-ready seal (ML-DSA-65 + Ed25519) issued when the document was completed.',
    verifying: 'Checking seal…',
    verify_failed: 'Seal check failed.',
    valid: 'Seal valid',
    invalid: 'Seal invalid',
    no_seal: 'No seal found',
    failed: 'failed',
    check_mldsa: 'ML-DSA-65 signature',
    check_ed25519: 'Ed25519 signature',
    check_hash: 'PDF matches sealed hash',
    check_key: 'Signing key recognised',
    check_root: 'Key certified by the sign.yt offline root key',
    revoked: 'The seal key was revoked on {date}, so this seal is no longer accepted. The document timestamp and Bitcoin anchor still prove when the file existed.',
    admin_title: 'Post-quantum seal',
    admin_backend: 'Key storage',
    backend_file: 'Encrypted key file on the server',
    backend_aws_kms: 'AWS KMS (hardware security module)',
    admin_root: 'Offline root key',
    root_missing: 'not pinned yet, see docs/PQC.md',
    root_pinned: 'pinned in the app',
    certified: 'certified',
    not_certified: 'not certified',
    key_revoked: 'revoked',
    current: 'current',
    fingerprint: 'Fingerprint',
    paste_label: 'Certificate or revocation from the root key (JSON)',
    paste_save: 'Save',
    saved: 'Saved.',
    invalid_json: 'This is not valid JSON.',
    rotate: 'Rotate key',
    rotate_confirm: 'Generate a new seal key? Existing seals stay verifiable; certify the new key with the root key afterwards.',
    upload_seal: 'Verify against a seal file (.json)',
    seal_selected: 'Seal: {name}',
  },
  delegate: {
    open: 'Forward to someone else',
    title: 'Forward for signing',
    description: 'The person you name gets their own signing link. Your link stops working.',
    phone: 'Mobile phone',
    phone_hint: 'Optional. International format, e.g. +48500100200.',
    phone_required_hint: 'Required: this document uses SMS verification.',
    phone_invalid: 'Use international format, e.g. +48500100200.',
    reason: 'Reason (optional)',
    submit: 'Forward',
    err_not_allowed: 'The sender does not allow forwarding this document.',
    err_limit: 'This document has been forwarded too many times.',
    err_already_recipient: 'This person is already a recipient of the document.',
    err_phone_required: 'A mobile number is required for SMS verification.',
    err_verify_first: 'Complete identity verification before forwarding.',
  },
  signing: {
    report_link: 'I did not expect this document',
    report_title: 'Report this request?',
    report_confirm: 'The sender is notified, the document is declined and this link stops working.',
    report_action: 'Report and decline',
    invalid_title: 'This link is no longer valid',
    invalid_body: 'It may have been forwarded, reported or replaced by a newer link. Check your inbox for the latest email.',
    delegated_title: 'Forwarded',
    delegated_body: 'The new signer has been emailed a link. You can close this page.',
    reported_title: 'Thanks for reporting',
    reported_body: 'The sender has been notified and this link no longer works.',
    not_your_turn: 'It is not your turn yet. You will get an email when the previous signer finishes.',
    signature_image_invalid: 'The signature image is invalid. Draw it again (PNG, max 1 MB).',
  },
  tpl: {
    edit_title: 'Edit template',
    read_only: 'You can view this template but not change it.',
    details: 'Details',
    name: 'Name',
    description: 'Description',
    category: 'Category',
    visibility: 'Visibility',
    type_PRIVATE: 'Only me',
    type_TEAM_SHARED: 'My team',
    type_ORG_SHARED: 'Whole organization',
    type_PUBLIC_LINK: 'Public link',
    team: 'Team',
    pick_team: 'Choose a team',
    team_required: 'Choose a team for a team-shared template.',
    pdf: 'PDF',
    roles_fields: 'Roles & fields',
    roles_fields_hint: '{n} fields placed. Define signer roles and place fields on the PDF.',
    edit_fields: 'Edit roles & fields',
    pdf_none: 'This template has no PDF yet.',
    pdf_swap_hint: 'Upload a new version. All {n} fields keep their positions, so the layout must match.',
    replace_pdf: 'Replace PDF',
    upload_pdf: 'Upload PDF',
    pdf_replaced: 'PDF replaced, fields kept',
    fields_out_of_range: 'The new PDF has {pages} pages, but {n} fields sit on later pages: {list}.',
    invalid_pdf: 'This file is not a valid PDF (encrypted PDFs are not supported).',
    too_large: 'The file is too large for your plan.',
    public_link: 'Public link',
    publish_forbidden: 'Only an org or team admin can enable public links.',
    public_link_hint: 'Anyone with the link can fill and sign this template. Each submission creates a new document.',
    link_on: 'On',
    link_off: 'Off',
    uses: 'Uses',
    expires: 'Expires',
    never: 'Never',
    expires_at: 'Expires at (optional)',
    max_uses: 'Max uses (optional)',
    enable: 'Enable link',
    rotate: 'Generate new link',
    disable: 'Disable link',
    disable_title: 'Disable public link?',
    disable_confirm: 'The link and QR code stop working immediately.',
    qr_alt: 'QR code for the public link',
    err_no_pdf: 'Upload a PDF before enabling the public link.',
    err_no_signer: 'Add a signer role to the template first.',
    err_roles_incomplete: 'Fill in name and email for every role except the public signer.',
  },
  public_tpl: {
    intro: 'Enter your details to sign as {role}. We email you a secure signing link.',
    phone_hint: 'Used to send your SMS verification code.',
    continue: 'Continue to signing',
    privacy: 'Your details are only shared with the sender of this document.',
    done_title: 'Check your email',
    done_body: 'We sent a signing link to {email}.',
    sign_now: 'Sign now',
    unavailable_title: 'Link unavailable',
    unavailable_body: 'This link has expired, reached its limit or was disabled by the sender.',
    err_captcha: 'Please complete the security check.',
    err_phone: 'A mobile number is required.',
    err_quota: 'The sender cannot accept new documents right now.',
    err_rate: 'Too many attempts. Wait a moment and try again.',
  },
  billing: {
    nav: 'Billing',
    title: 'Billing',
    subtitle: 'Plan, usage and invoices for your organization.',
    manage: 'Manage billing',
    disabled: 'Online billing is not enabled on this platform. Contact support to change your plan.',
    read_only_title: 'Account is read-only',
    read_only_body: 'Payment is overdue since {date}. Creating and sending documents is paused until it is settled.',
    fix_payment: 'Update payment',
    past_due: 'Your last payment failed. Update your payment method to avoid interruption.',
    cancels_on: 'Your subscription ends on {date}.',
    near_limit_title: 'You are close to your plan limit',
    near_limit_body: 'Over 80% of this period’s quota is used. Upgrade to keep sending.',
    near_limit_overage: 'Over 80% of this period’s quota is used. Extra documents are billed on your next invoice.',
    current_plan: 'Current plan',
    period: 'Billing period',
    teams: 'Teams',
    extra_teams: '+{n} extra',
    unlimited: 'Unlimited',
    not_in_plan: 'Not included in plan',
    usage: 'Usage this period',
    documents: 'Documents',
    sms: 'SMS',
    overage: '{n} documents over the limit this period.',
    choose_plan: 'Choose a plan',
    choose_plan_hint: 'You are redirected to Stripe to pay. Prices include the details shown at checkout.',
    change_plan: 'Change plan',
    change_plan_hint: 'Plan changes and cancellation happen in the Stripe customer portal.',
    current: 'Current',
    subscribe: 'Subscribe',
    switch: 'Change in portal',
    redirect_failed: 'Could not open Stripe',
    invoices: 'Invoices',
    no_invoices: 'No invoices yet',
    col_number: 'Number',
    col_date: 'Issued',
    col_gross: 'Gross',
    col_files: 'Files',
    download_aria: 'Download {kind} for invoice {n}',
    tier_STARTER: 'Starter',
    tier_PROFESSIONAL: 'Professional',
    tier_BUSINESS: 'Business',
    tier_ENTERPRISE: 'Enterprise',
    tier_STARTER_desc: '50 documents a month, 3 users, email verification.',
    tier_PROFESSIONAL_desc: '500 documents, 10 users, SMS, custom domain, your branding.',
    tier_BUSINESS_desc: 'Unlimited documents and users, SSO, API, webhooks, own email provider.',
    status_ACTIVE: 'Active',
    status_TRIALING: 'Trial',
    status_PAST_DUE: 'Past due',
    status_CANCELLED: 'Cancelled',
    status_INCOMPLETE: 'Incomplete',
    ksef_PENDING: 'Queued',
    ksef_SENT: 'Sent',
    ksef_ACCEPTED: 'Accepted',
    ksef_REJECTED: 'Rejected',
    ksef_FAILED: 'Failed',
  },
  webhooks: {
    deliveries: 'Delivery log',
    no_deliveries: 'No deliveries yet.',
    attempt: 'attempt {n}',
    next_retry: 'Next retry {date}',
    redeliver: 'Redeliver',
    redelivery_queued: 'Redelivery queued',
    details: 'Show details',
    payload: 'Payload',
    response: 'Response body',
    status_PENDING: 'Pending',
    status_SUCCESS: 'Delivered',
    status_FAILED: 'Failed',
    status_RETRYING: 'Retrying',
    status_DEAD: 'Gave up',
    secret_once_title: 'Copy your signing secret now',
    secret_once_body: 'Use it to verify the X-Signyt-Signature header. It will not be shown again.',
  },
  email: {
    ses_desc: 'Send through your AWS account',
    mailgun_desc: 'Send through your Mailgun domain',
    access_key_id: 'Access key ID',
    secret_access_key: 'Secret access key',
    region: 'Region',
    ses_hint: 'The IAM user needs ses:SendRawEmail, and the from address or its domain must be verified in SES.',
    api_key: 'API key',
    sending_domain: 'Sending domain',
  },
  bounce: {
    title: 'Bounce notifications',
    url_label: 'Bounce webhook URL',
    ses_hint: 'In AWS SNS create an HTTPS subscription with this URL (raw message delivery off) on the topic your SES identity sends bounce and complaint notifications to. sign.yt confirms the subscription automatically.',
    mailgun_hint: 'In Mailgun → Webhooks add this URL for the "Permanent failure" and "Spam complaints" events.',
    mailgun_signing_key: 'HTTP webhook signing key',
    mailgun_signing_key_hint: 'Mailgun → Settings → API security. Used to verify that bounce events come from your Mailgun account.',
    mailgun_key_missing: 'Add the webhook signing key above, otherwise bounce events are rejected.',
  },
  dns: {
    title: 'DNS records',
    subtitle: 'Add these records at your DNS provider for {domain} so mail is not marked as spam.',
    check: 'Check DNS',
    ok: 'OK',
    missing: 'Not found',
    unchecked: 'Not checked',
    host: 'Host',
    value: 'Value',
    copy_host: 'Copy {purpose} host',
    copy_value: 'Copy {purpose} value',
    found: 'Found instead:',
  },
  api_keys: {
    umbrella_hint: 'admin grants every scope.',
    expires: 'Expires on (optional)',
  },
  redirect: {
    advanced: 'Advanced options',
    label: 'Redirect after signing',
    hint: 'Optional https:// URL. Signers go there after signing, with ?document_id=…&status=signed appended.',
    invalid: 'Enter a full https:// URL.',
    notice: 'After signing you will be redirected to {host}.',
    countdown: 'Redirecting to {host} in {n} s…',
    go_now: 'Continue to {host}',
  },
  integration: {
    title: 'Signing integration',
    subtitle: 'Redirects after signing and embedding the signing page on your website.',
    redirect_domains: 'Allowed redirect domains',
    redirect_domains_hint: 'One domain per line, subdomains included. Empty: any https URL is allowed and signers see where they are sent.',
    embed_origins: 'Embedding websites',
    embed_origins_hint: 'One https origin per line, e.g. https://portal.example.com. Only these sites may show /embed/sign/… links in an iframe.',
    save: 'Save',
    saved: 'Signing integration saved',
  },
  register: {
    check_email: 'Check your inbox. We sent instructions to finish signing up.',
    check_email_title: 'Check your inbox',
    check_email_body: 'We sent a link to {email}. Open it to set your password and finish creating your account. It is valid for 24 hours.',
  },
  signup_finish: {
    title: 'Finish creating your account',
    subtitle: 'Signing up as {email}. Choose a password to continue.',
    submit: 'Create account',
    creating: 'Creating…',
    invalid_title: 'Link invalid or expired',
    invalid_body: 'This sign-up link was already used or has expired. Sign up again to get a new one.',
    sign_up_again: 'Sign up again',
  },
  plan: {
    trial_title: 'Free trial',
    trial_days: 'Your trial ends in {n} day | Your trial ends in {n} days',
    trial_body: 'You have Professional features until {date}.',
    trial_expired_title: 'Your free trial has ended',
    trial_expired_body: 'Documents stay available to view and download. Choose a plan to create and send again.',
    past_due_title: 'Account is read-only',
    past_due_body: 'Payment is overdue. Creating and sending documents is paused until it is settled.',
    upgrade: 'Choose a plan',
    locked_title: 'Not included in your plan',
    locked_body: '{feature} is available on a higher plan.',
    feature: {
      sms: 'SMS verification',
      custom_domain: 'Custom domain',
      webhooks: 'Webhooks',
      api_keys: 'API keys',
      sso: 'Single sign-on',
      embedded: 'Embedded signing',
    },
    err: {
      trial_expired: 'Your free trial has ended. Choose a plan in Billing to keep sending.',
      document_no_pdf: 'This document has no PDF to copy. Cancel it without a copy and create a new document.',
      intake_pending: 'The start form must be completed before signing.',
      void_certificate_not_found: 'No void certificate: this document was not cancelled.',
      subscription_past_due: 'Payment overdue — the organization is read-only until the invoice is paid.',
      feature_not_in_plan: 'This feature is not included in your plan. Upgrade in Billing.',
      sms_not_in_plan: 'SMS verification is not included in your plan. Use email verification or upgrade.',
      sms_quota_exceeded: 'Monthly SMS allowance used up. Upgrade your plan or use email verification.',
      sms_unavailable: 'SMS verification is unavailable for this document. Please contact the sender.',
      teams: 'Team limit reached for your plan. Upgrade or buy extra teams in Billing.',
      email_not_verified: "Confirm your email address first — use 'Resend email' in the banner at the top.",
      invite_daily_cap: 'Daily invitation limit for new accounts reached. Try again tomorrow, choose a plan, or connect your own email provider.',
      reserved_brand_name: 'This name looks like a protected brand or public institution. Contact support if you represent it.',
    },
  },
}

type Messages = typeof en

const pl: Messages = {
  common: {
    cancel: 'Anuluj', save: 'Zapisz', saved: 'Zapisano', copy: 'Kopiuj', copied: 'Skopiowano', dismiss: 'Zamknij',
    refresh: 'Odśwież', load_more: 'Pokaż więcej', failed: 'Coś poszło nie tak. Spróbuj ponownie.',
    load_failed: 'Nie udało się wczytać danych.', email: 'E-mail', role: 'Rola', full_name: 'Imię i nazwisko',
    password: 'Hasło', go_to_app: 'Przejdź do panelu', go_to_sign_in: 'Przejdź do logowania',
  },
  roles: { admin: 'Administrator', member: 'Członek' },
  team_roles: { TEAM_ADMIN: 'Admin zespołu', TEAM_MANAGER: 'Menedżer', TEAM_MEMBER: 'Członek', TEAM_VIEWER: 'Podgląd' },
  invite: {
    title: 'Zaproszenie',
    invalid: 'Ten link z zaproszeniem jest nieprawidłowy lub został już użyty.',
    expired: 'Zaproszenie wygasło. Poproś nadawcę o nowe.',
    join: 'Dołącz do {org}',
    invited_by: '{name} zaprasza Cię.',
    team: 'Zespół: {team}.',
    expires: 'Wygasa',
    accept: 'Przyjmij zaproszenie',
    mismatch: 'Jesteś zalogowany jako {current}, a zaproszenie jest dla {invited}.',
    mismatch_short: 'To zaproszenie wysłano na inny adres e-mail.',
    switch_account: 'Wyloguj i użyj innego konta',
    sign_in_hint: 'Masz już konto sign.yt z tym adresem. Zaloguj się, aby przyjąć zaproszenie.',
    sign_in_to_accept: 'Zaloguj się i przyjmij',
    create_hint: 'Załóż konto, aby dołączyć. Adres e-mail wynika z zaproszenia.',
    password_hint: 'Co najmniej 10 znaków.',
    password_short: 'Hasło musi mieć co najmniej 10 znaków.',
    create_and_join: 'Załóż konto i dołącz',
    account_exists: 'Konto z tym adresem już istnieje. Zaloguj się.',
  },
  verify_email: {
    banner: 'Potwierdź adres {email}, aby zachować pełny dostęp.',
    resend: 'Wyślij ponownie',
    sent: 'Wysłaliśmy e-mail weryfikacyjny na {email}.',
    checking: 'Potwierdzamy adres e-mail…',
    ok_title: 'Adres potwierdzony',
    ok_body: 'Dziękujemy. Twój adres e-mail jest zweryfikowany.',
    error_title: 'Link nieprawidłowy lub wygasł',
    error_body: 'Zaloguj się i wyślij nowy e-mail weryfikacyjny z paska u góry.',
  },
  team_invite: {
    title: 'Zaproś osoby',
    subtitle: 'Zaproszeni dostaną link e-mailem. Obecni członkowie organizacji są dodawani od razu.',
    team: 'Zespół (opcjonalnie)',
    team_role: 'Rola w zespole',
    no_team: 'Bez zespołu',
    send: 'Wyślij zaproszenie',
    sent: 'Zaproszenie wysłane do {email}',
    added: '{email} dodano do zespołu',
    failed: 'Nie udało się wysłać zaproszenia',
    pending: 'Oczekujące zaproszenia ({n})',
    none: 'Brak oczekujących zaproszeń.',
    sent_on: 'Wysłano {date} · wygasa {exp}',
    resend: 'Wyślij ponownie',
    revoke: 'Cofnij',
    revoke_aria: 'Cofnij zaproszenie dla {email}',
    revoke_title: 'Cofnąć zaproszenie?',
    revoke_confirm: 'Link wysłany do {email} przestanie działać.',
  },
  sessions: {
    title: 'Aktywne sesje',
    subtitle: 'Urządzenia, na których jesteś zalogowany.',
    this_device: 'To urządzenie',
    last_active: 'Ostatnia aktywność {date}',
    unknown_device: 'Nieznane urządzenie',
    browser_on_os: '{browser} na {os}',
    revoke: 'Wyloguj',
    revoke_aria: 'Wyloguj {device}',
    revoke_all: 'Wyloguj inne urządzenia',
    revoke_all_confirm: 'Wszystkie sesje poza bieżącą zostaną wylogowane.',
    revoked_all: 'Wylogowano inne urządzenia',
  },
  rodo: {
    email_export_link: 'Wyślij mi link do pobrania',
    email_delete_link: 'Wyślij mi link do usunięcia',
    proof_sent_title: 'Sprawdź skrzynkę',
    proof_sent: 'Wysłaliśmy jednorazowy link potwierdzający na {email}. Wygasa po 1 godzinie.',
    action: 'Rodzaj żądania',
    action_export: 'Eksport moich danych',
    action_delete: 'Usunięcie konta',
    delete_title: 'Usuń konto',
    delete_body: 'Twoje dane osobowe zostaną zanonimizowane. Podpisane dokumenty są archiwizowane zgodnie z prawem. Tej operacji nie można cofnąć.',
    type_delete: 'Wpisz DELETE, aby potwierdzić',
    delete_confirm_btn: 'Usuń moje konto',
    deleted: 'Konto zostało usunięte, a dane zanonimizowane.',
  },
  orgdata: {
    tab: 'Dane i prywatność',
    export_title: 'Eksport danych organizacji',
    export_body: 'Jeden plik ZIP ze wszystkimi dokumentami (oryginalne i podpisane PDF), ścieżkami audytu, pełnym dziennikiem zdarzeń, odbiorcami, szablonami, kontaktami i ustawieniami (bez danych dostępowych). Wyślemy Ci e-mailem link ważny 7 dni, który wygasa 10 minut po pierwszym pobraniu.',
    export_btn: 'Zamów eksport',
    requested: 'Zamówiono {date}',
    size: 'Rozmiar: {size}',
    status_PENDING: 'W kolejce…',
    status_RUNNING: 'Przygotowywanie… {progress}%',
    status_READY: 'Gotowe. Link do pobrania jest w Twojej skrzynce (wygasa {date}).',
    status_DOWNLOADED: 'Pobrano {date}.',
    status_EXPIRED: 'Link do pobrania wygasł.',
    status_FAILED: 'Eksport nie powiódł się. Spróbuj ponownie.',
    status_FAILED_EMAIL: 'Gotowe, ale nie udało się wysłać linku. Wyślij go ponownie.',
    resend_btn: 'Wyślij link ponownie',
    resent: 'Link do pobrania wysłany',
    step_up_sent: 'Wysłaliśmy Ci e-mailem kod potwierdzający. Wpisz go, aby kontynuować.',
    download_hint: 'Pobieranie rozpocznie się w przeglądarce. Jeśli zostanie przerwane, użyj przycisku ponownie w ciągu 10 minut.',
    in_progress: 'Eksport jest już przygotowywany.',
    rate_limited: 'Zbyt wiele prób. Spróbuj za kilka minut.',
    mfa_label: 'Kod uwierzytelniający',
    mfa_hint: 'Wpisz kod uwierzytelniający, aby potwierdzić.',
    mfa_invalid: 'Nieprawidłowy kod. Spróbuj ponownie.',
    close_title: 'Zamknij organizację',
    close_body: 'Natychmiast blokuje dostęp wszystkim członkom, anuluje subskrypcję i po 30-dniowym okresie karencji trwale usuwa wszystkie dokumenty i dane. Najpierw wyeksportuj dane.',
    close_type: 'Wpisz {name}, aby potwierdzić',
    close_btn: 'Zamknij organizację',
    close_confirm: 'Wszyscy członkowie natychmiast stracą dostęp. Nie możesz tego cofnąć samodzielnie.',
    closed: 'Organizacja zamknięta. Trwa wylogowanie.',
    name_mismatch: 'Nazwa się nie zgadza.',
    download_title: 'Eksport organizacji',
    download_btn: 'Pobierz eksport',
    link_invalid: 'Ten link jest nieprawidłowy, wygasł lub został już użyty. Zamów nowy eksport w Ustawienia, Dane i prywatność.',
    closure_badge: 'Zgłoszono zamknięcie',
    closure_hint: 'Zamknięta przez administratora organizacji {date}. Włącz organizację, aby anulować zamknięcie przed datą usunięcia danych.',
  },
  pq: {
    title: 'Pieczęć dowodowa',
    badge: 'Pieczęć dowodowa gotowa na post-kwantową kryptografię',
    explainer: 'Skrót końcowego PDF i ścieżka audytu są zapieczętowane algorytmami ML-DSA-65 i Ed25519. To dowód integralności, a nie kwalifikowany podpis elektroniczny.',
    alg: 'Algorytm',
    kid: 'ID klucza',
    download: 'Pobierz pieczęć (.json)',
    verify_link: 'Zweryfikuj kopię',
    verify_title: 'Post-kwantowa pieczęć dowodowa',
    verify_explainer: 'Sprawdza pieczęć gotową na post-kwantową kryptografię (ML-DSA-65 + Ed25519) wystawioną przy zakończeniu dokumentu.',
    verifying: 'Sprawdzanie pieczęci…',
    verify_failed: 'Nie udało się sprawdzić pieczęci.',
    valid: 'Pieczęć prawidłowa',
    invalid: 'Pieczęć nieprawidłowa',
    no_seal: 'Brak pieczęci',
    failed: 'błąd',
    check_mldsa: 'Podpis ML-DSA-65',
    check_ed25519: 'Podpis Ed25519',
    check_hash: 'PDF zgodny z zapieczętowanym skrótem',
    check_key: 'Klucz rozpoznany',
    check_root: 'Klucz poświadczony offline\'owym kluczem głównym sign.yt',
    revoked: 'Klucz pieczęci unieważniono {date}, więc ta pieczęć nie jest już uznawana. Znacznik czasu dokumentu i kotwica w Bitcoinie nadal dowodzą, kiedy plik istniał.',
    admin_title: 'Pieczęć post-kwantowa',
    admin_backend: 'Przechowywanie klucza',
    backend_file: 'Zaszyfrowany plik klucza na serwerze',
    backend_aws_kms: 'AWS KMS (sprzętowy sejf HSM)',
    admin_root: 'Klucz główny offline',
    root_missing: 'jeszcze nie wgrany, zobacz docs/PQC.md',
    root_pinned: 'wbudowany w aplikację',
    certified: 'poświadczony',
    not_certified: 'niepoświadczony',
    key_revoked: 'unieważniony',
    current: 'bieżący',
    fingerprint: 'Odcisk',
    paste_label: 'Poświadczenie lub unieważnienie z klucza głównego (JSON)',
    paste_save: 'Zapisz',
    saved: 'Zapisano.',
    invalid_json: 'To nie jest poprawny JSON.',
    rotate: 'Wymień klucz',
    rotate_confirm: 'Wygenerować nowy klucz pieczęci? Dotychczasowe pieczęcie dalej się zweryfikują; nowy klucz poświadcz potem kluczem głównym.',
    upload_seal: 'Zweryfikuj z pliku pieczęci (.json)',
    seal_selected: 'Pieczęć: {name}',
  },
  delegate: {
    open: 'Przekaż innej osobie',
    title: 'Przekaż do podpisu',
    description: 'Wskazana osoba dostanie własny link do podpisu. Twój link przestanie działać.',
    phone: 'Telefon komórkowy',
    phone_hint: 'Opcjonalnie. Format międzynarodowy, np. +48500100200.',
    phone_required_hint: 'Wymagany: dokument używa weryfikacji SMS.',
    phone_invalid: 'Użyj formatu międzynarodowego, np. +48500100200.',
    reason: 'Powód (opcjonalnie)',
    submit: 'Przekaż',
    err_not_allowed: 'Nadawca nie zezwala na przekazanie tego dokumentu.',
    err_limit: 'Ten dokument przekazywano już zbyt wiele razy.',
    err_already_recipient: 'Ta osoba jest już odbiorcą dokumentu.',
    err_phone_required: 'Do weryfikacji SMS wymagany jest numer telefonu.',
    err_verify_first: 'Najpierw dokończ weryfikację tożsamości.',
  },
  signing: {
    report_link: 'Nie spodziewałem się tego dokumentu',
    report_title: 'Zgłosić to żądanie?',
    report_confirm: 'Nadawca zostanie powiadomiony, dokument odrzucony, a ten link przestanie działać.',
    report_action: 'Zgłoś i odrzuć',
    invalid_title: 'Ten link jest już nieaktualny',
    invalid_body: 'Mógł zostać przekazany, zgłoszony lub zastąpiony nowszym. Sprawdź najnowszą wiadomość w skrzynce.',
    delegated_title: 'Przekazano',
    delegated_body: 'Nowy podpisujący dostał link e-mailem. Możesz zamknąć tę stronę.',
    reported_title: 'Dziękujemy za zgłoszenie',
    reported_body: 'Nadawca został powiadomiony, a ten link już nie działa.',
    not_your_turn: 'To jeszcze nie Twoja kolej. Dostaniesz e-mail, gdy poprzednia osoba podpisze.',
    signature_image_invalid: 'Obraz podpisu jest nieprawidłowy. Narysuj go ponownie (PNG, maks. 1 MB).',
  },
  tpl: {
    edit_title: 'Edytuj szablon',
    read_only: 'Możesz przeglądać ten szablon, ale nie możesz go zmieniać.',
    details: 'Szczegóły',
    name: 'Nazwa',
    description: 'Opis',
    category: 'Kategoria',
    visibility: 'Widoczność',
    type_PRIVATE: 'Tylko ja',
    type_TEAM_SHARED: 'Mój zespół',
    type_ORG_SHARED: 'Cała organizacja',
    type_PUBLIC_LINK: 'Link publiczny',
    team: 'Zespół',
    pick_team: 'Wybierz zespół',
    team_required: 'Wybierz zespół dla szablonu zespołowego.',
    pdf: 'PDF',
    roles_fields: 'Role i pola',
    roles_fields_hint: 'Rozmieszczone pola: {n}. Zdefiniuj role podpisujących i rozmieść pola na PDF.',
    edit_fields: 'Edytuj role i pola',
    pdf_none: 'Ten szablon nie ma jeszcze pliku PDF.',
    pdf_swap_hint: 'Wgraj nową wersję. Wszystkie pola ({n}) zachowają pozycje, więc układ musi być taki sam.',
    replace_pdf: 'Podmień PDF',
    upload_pdf: 'Wgraj PDF',
    pdf_replaced: 'PDF podmieniony, pola zachowane',
    fields_out_of_range: 'Nowy PDF ma {pages} str., a {n} pól leży na dalszych stronach: {list}.',
    invalid_pdf: 'To nie jest prawidłowy PDF (zaszyfrowane pliki nie są obsługiwane).',
    too_large: 'Plik jest za duży dla Twojego planu.',
    public_link: 'Link publiczny',
    publish_forbidden: 'Tylko administrator organizacji lub zespołu może włączyć link publiczny.',
    public_link_hint: 'Każdy z linkiem może wypełnić i podpisać ten szablon. Każde zgłoszenie tworzy nowy dokument.',
    link_on: 'Włączony',
    link_off: 'Wyłączony',
    uses: 'Użycia',
    expires: 'Wygasa',
    never: 'Nigdy',
    expires_at: 'Wygasa (opcjonalnie)',
    max_uses: 'Limit użyć (opcjonalnie)',
    enable: 'Włącz link',
    rotate: 'Wygeneruj nowy link',
    disable: 'Wyłącz link',
    disable_title: 'Wyłączyć link publiczny?',
    disable_confirm: 'Link i kod QR natychmiast przestaną działać.',
    qr_alt: 'Kod QR linku publicznego',
    err_no_pdf: 'Wgraj PDF przed włączeniem linku publicznego.',
    err_no_signer: 'Najpierw dodaj do szablonu rolę podpisującego.',
    err_roles_incomplete: 'Uzupełnij imię i e-mail dla każdej roli poza publicznym podpisującym.',
  },
  public_tpl: {
    intro: 'Podaj swoje dane, aby podpisać jako {role}. Wyślemy Ci bezpieczny link do podpisu.',
    phone_hint: 'Na ten numer wyślemy kod weryfikacyjny SMS.',
    continue: 'Przejdź do podpisu',
    privacy: 'Twoje dane trafią wyłącznie do nadawcy tego dokumentu.',
    done_title: 'Sprawdź skrzynkę',
    done_body: 'Wysłaliśmy link do podpisu na {email}.',
    sign_now: 'Podpisz teraz',
    unavailable_title: 'Link niedostępny',
    unavailable_body: 'Link wygasł, osiągnął limit lub został wyłączony przez nadawcę.',
    err_captcha: 'Dokończ weryfikację zabezpieczającą.',
    err_phone: 'Numer telefonu jest wymagany.',
    err_quota: 'Nadawca nie może teraz przyjmować nowych dokumentów.',
    err_rate: 'Zbyt wiele prób. Odczekaj chwilę i spróbuj ponownie.',
  },
  billing: {
    nav: 'Płatności',
    title: 'Płatności',
    subtitle: 'Plan, wykorzystanie i faktury Twojej organizacji.',
    manage: 'Zarządzaj płatnościami',
    disabled: 'Płatności online nie są włączone na tej platformie. Skontaktuj się z obsługą, aby zmienić plan.',
    read_only_title: 'Konto w trybie tylko do odczytu',
    read_only_body: 'Płatność jest zaległa od {date}. Tworzenie i wysyłka dokumentów są wstrzymane do czasu uregulowania.',
    fix_payment: 'Zaktualizuj płatność',
    past_due: 'Ostatnia płatność nie powiodła się. Zaktualizuj metodę płatności, aby uniknąć przerwy.',
    cancels_on: 'Subskrypcja kończy się {date}.',
    near_limit_title: 'Zbliżasz się do limitu planu',
    near_limit_body: 'Wykorzystano ponad 80% limitu w tym okresie. Zmień plan, aby dalej wysyłać.',
    near_limit_overage: 'Wykorzystano ponad 80% limitu. Dodatkowe dokumenty doliczymy do następnej faktury.',
    current_plan: 'Obecny plan',
    period: 'Okres rozliczeniowy',
    teams: 'Zespoły',
    extra_teams: '+{n} dodatkowe',
    unlimited: 'Bez limitu',
    not_in_plan: 'Nie wchodzi w plan',
    usage: 'Wykorzystanie w tym okresie',
    documents: 'Dokumenty',
    sms: 'SMS',
    overage: '{n} dokumentów ponad limit w tym okresie.',
    choose_plan: 'Wybierz plan',
    choose_plan_hint: 'Płatność odbywa się w Stripe. Cena i szczegóły są widoczne przy zamówieniu.',
    change_plan: 'Zmień plan',
    change_plan_hint: 'Zmiana planu i rezygnacja odbywają się w portalu klienta Stripe.',
    current: 'Obecny',
    subscribe: 'Wykup',
    switch: 'Zmień w portalu',
    redirect_failed: 'Nie udało się otworzyć Stripe',
    invoices: 'Faktury',
    no_invoices: 'Brak faktur',
    col_number: 'Numer',
    col_date: 'Data wystawienia',
    col_gross: 'Brutto',
    col_files: 'Pliki',
    download_aria: 'Pobierz {kind} faktury {n}',
    tier_STARTER: 'Starter',
    tier_PROFESSIONAL: 'Professional',
    tier_BUSINESS: 'Business',
    tier_ENTERPRISE: 'Enterprise',
    tier_STARTER_desc: '50 dokumentów miesięcznie, 3 użytkowników, weryfikacja e-mail.',
    tier_PROFESSIONAL_desc: '500 dokumentów, 10 użytkowników, SMS, własna domena i branding.',
    tier_BUSINESS_desc: 'Bez limitu dokumentów i użytkowników, SSO, API, webhooki, własny dostawca poczty.',
    status_ACTIVE: 'Aktywna',
    status_TRIALING: 'Okres próbny',
    status_PAST_DUE: 'Zaległa',
    status_CANCELLED: 'Anulowana',
    status_INCOMPLETE: 'Niekompletna',
    ksef_PENDING: 'W kolejce',
    ksef_SENT: 'Wysłana',
    ksef_ACCEPTED: 'Przyjęta',
    ksef_REJECTED: 'Odrzucona',
    ksef_FAILED: 'Błąd',
  },
  webhooks: {
    deliveries: 'Historia dostarczeń',
    no_deliveries: 'Brak dostarczeń.',
    attempt: 'próba {n}',
    next_retry: 'Kolejna próba {date}',
    redeliver: 'Wyślij ponownie',
    redelivery_queued: 'Ponowna wysyłka w kolejce',
    details: 'Pokaż szczegóły',
    payload: 'Treść',
    response: 'Odpowiedź serwera',
    status_PENDING: 'Oczekuje',
    status_SUCCESS: 'Dostarczono',
    status_FAILED: 'Błąd',
    status_RETRYING: 'Ponawianie',
    status_DEAD: 'Porzucono',
    secret_once_title: 'Skopiuj teraz sekret podpisu',
    secret_once_body: 'Służy do weryfikacji nagłówka X-Signyt-Signature. Nie pokażemy go ponownie.',
  },
  email: {
    ses_desc: 'Wysyłka przez Twoje konto AWS',
    mailgun_desc: 'Wysyłka przez Twoją domenę w Mailgun',
    access_key_id: 'Access key ID',
    secret_access_key: 'Secret access key',
    region: 'Region',
    ses_hint: 'Użytkownik IAM potrzebuje uprawnienia ses:SendRawEmail, a adres nadawcy lub domena muszą być zweryfikowane w SES.',
    api_key: 'Klucz API',
    sending_domain: 'Domena wysyłkowa',
  },
  bounce: {
    title: 'Powiadomienia o odbiciach',
    url_label: 'Adres webhooka odbić',
    ses_hint: 'W AWS SNS utwórz subskrypcję HTTPS z tym adresem (bez raw message delivery) w temacie, do którego tożsamość SES wysyła powiadomienia o odbiciach i skargach. sign.yt potwierdzi subskrypcję automatycznie.',
    mailgun_hint: 'W Mailgun → Webhooks dodaj ten adres dla zdarzeń „Permanent failure” i „Spam complaints”.',
    mailgun_signing_key: 'Klucz podpisu webhooków HTTP',
    mailgun_signing_key_hint: 'Mailgun → Settings → API security. Służy do sprawdzenia, że zdarzenia pochodzą z Twojego konta Mailgun.',
    mailgun_key_missing: 'Dodaj powyżej klucz podpisu webhooków, inaczej zdarzenia o odbiciach będą odrzucane.',
  },
  dns: {
    title: 'Rekordy DNS',
    subtitle: 'Dodaj te rekordy u dostawcy DNS dla {domain}, aby poczta nie trafiała do spamu.',
    check: 'Sprawdź DNS',
    ok: 'OK',
    missing: 'Nie znaleziono',
    unchecked: 'Nie sprawdzono',
    host: 'Host',
    value: 'Wartość',
    copy_host: 'Kopiuj host {purpose}',
    copy_value: 'Kopiuj wartość {purpose}',
    found: 'Znaleziono zamiast tego:',
  },
  api_keys: {
    umbrella_hint: 'admin daje wszystkie uprawnienia.',
    expires: 'Wygasa (opcjonalnie)',
  },
  redirect: {
    advanced: 'Opcje zaawansowane',
    label: 'Przekierowanie po podpisie',
    hint: 'Opcjonalny adres https://. Po podpisie sygnatariusz trafi tam z dopisanym ?document_id=…&status=signed.',
    invalid: 'Podaj pełny adres https://.',
    notice: 'Po podpisaniu nastąpi przekierowanie do {host}.',
    countdown: 'Przekierowanie do {host} za {n} s…',
    go_now: 'Przejdź do {host}',
  },
  integration: {
    title: 'Integracja podpisu',
    subtitle: 'Przekierowania po podpisie i osadzanie strony podpisu w Twojej witrynie.',
    redirect_domains: 'Dozwolone domeny przekierowań',
    redirect_domains_hint: 'Jedna domena w wierszu, z subdomenami. Puste: dowolny adres https, a sygnatariusz widzi, dokąd trafi.',
    embed_origins: 'Witryny osadzające',
    embed_origins_hint: 'Jeden adres https w wierszu, np. https://portal.example.com. Tylko te witryny mogą wyświetlać linki /embed/sign/… w ramce iframe.',
    save: 'Zapisz',
    saved: 'Zapisano integrację podpisu',
  },
  register: {
    check_email: 'Sprawdź skrzynkę. Wysłaliśmy instrukcje dokończenia rejestracji.',
    check_email_title: 'Sprawdź skrzynkę',
    check_email_body: 'Wysłaliśmy link na {email}. Otwórz go, aby ustawić hasło i dokończyć zakładanie konta. Link jest ważny 24 godziny.',
  },
  signup_finish: {
    title: 'Dokończ zakładanie konta',
    subtitle: 'Rejestrujesz się jako {email}. Ustaw hasło, aby kontynuować.',
    submit: 'Utwórz konto',
    creating: 'Tworzenie…',
    invalid_title: 'Link nieważny lub wygasł',
    invalid_body: 'Ten link rejestracyjny został już użyty lub wygasł. Zarejestruj się ponownie, aby otrzymać nowy.',
    sign_up_again: 'Zarejestruj się ponownie',
  },
  plan: {
    trial_title: 'Okres próbny',
    trial_days: 'Okres próbny kończy się za {n} dzień | Okres próbny kończy się za {n} dni',
    trial_body: 'Masz funkcje planu Professional do {date}.',
    trial_expired_title: 'Okres próbny się zakończył',
    trial_expired_body: 'Dokumenty możesz nadal przeglądać i pobierać. Wybierz plan, aby znów tworzyć i wysyłać.',
    past_due_title: 'Konto w trybie tylko do odczytu',
    past_due_body: 'Płatność jest zaległa. Tworzenie i wysyłka dokumentów są wstrzymane do czasu uregulowania.',
    upgrade: 'Wybierz plan',
    locked_title: 'Niedostępne w Twoim planie',
    locked_body: '{feature} jest dostępne w wyższym planie.',
    feature: {
      sms: 'Weryfikacja SMS',
      custom_domain: 'Własna domena',
      webhooks: 'Webhooki',
      api_keys: 'Klucze API',
      sso: 'Logowanie SSO',
      embedded: 'Osadzone podpisywanie',
    },
    err: {
      trial_expired: 'Okres próbny się zakończył. Wybierz plan w Płatnościach, aby dalej wysyłać.',
      document_no_pdf: 'Ten dokument nie ma pliku PDF do skopiowania. Anuluj go bez kopii i utwórz nowy dokument.',
      intake_pending: 'Przed podpisaniem trzeba wypełnić formularz startowy.',
      void_certificate_not_found: 'Brak zaświadczenia: ten dokument nie został anulowany.',
      subscription_past_due: 'Płatność zaległa — organizacja jest w trybie tylko do odczytu do czasu opłacenia faktury.',
      feature_not_in_plan: 'Ta funkcja nie jest dostępna w Twoim planie. Zmień plan w Płatnościach.',
      sms_not_in_plan: 'Weryfikacja SMS nie jest dostępna w Twoim planie. Użyj weryfikacji e-mail lub zmień plan.',
      sms_quota_exceeded: 'Wykorzystano miesięczny limit SMS. Zmień plan lub użyj weryfikacji e-mail.',
      sms_unavailable: 'Weryfikacja SMS jest niedostępna dla tego dokumentu. Skontaktuj się z nadawcą.',
      teams: 'Osiągnięto limit zespołów w Twoim planie. Zmień plan lub dokup zespoły w Płatnościach.',
      email_not_verified: "Najpierw potwierdź adres e-mail — użyj „Wyślij ponownie” w pasku u góry.",
      invite_daily_cap: 'Osiągnięto dzienny limit zaproszeń dla nowych kont. Spróbuj jutro, wybierz plan lub podłącz własnego dostawcę poczty.',
      reserved_brand_name: 'Ta nazwa przypomina chronioną markę lub instytucję publiczną. Skontaktuj się z obsługą, jeśli ją reprezentujesz.',
    },
  },
}

export const fxMessages = { en: { fx: en }, pl: { fx: pl } }

/** Component-local i18n with feature strings; falls back to global keys. */
export function useFx() {
  return useI18n({ useScope: 'local', inheritLocale: true, messages: fxMessages })
}

/** Plain lookup for places outside a component scope (nav labels). */
export function fxText<S extends keyof Messages>(locale: string, section: S, key: keyof Messages[S]): string {
  const m = (locale === 'pl' ? pl : en)[section]
  return String(m[key])
}

/** Localized text for a backend error `code` (plan rules), else undefined. */
export function fxErrorText(locale: string, code: string): string | undefined {
  const m = (locale === 'pl' ? pl : en).plan.err as Record<string, string>
  return m[code]
}
